import { useRef } from 'react';
import Button from '@v2/shared/components/Button';
import Container from '@v2/shared/components/Container';
import Currency from '@v2/shared/components/Currency';
import ReviewStars from '@v2/shared/components/ReviewStars';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLongArrowRight, faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import { Product } from '@v2/shared/lib/types';
import { useSite } from '@v2/shared/lib/site';
import useHeaderHeight from '@v2/shared/lib/useHeaderHeight';
import useParallax from './useParallax';
import ParallaxLayer from './ParallaxLayer';
import DigitalMag from './DigitalMag';
import zinioLogo from './images/zinio-logo.png';
import dots1 from './images/shapes/dots-1.png';
import dots2 from './images/shapes/dots-2.png';
import ellipse from './images/shapes/ellipse.png';
import natGeo from './images/covers/nat-geo.png';
import cooksIll from './images/covers/cooks-illustrated.png';
import newYork from './images/covers/new-york.png';
import motorTrend from './images/covers/motor-trend.png';
import natGeoKids from './images/covers/nat-geo-kids.png';
import newsweek from './images/covers/newsweek.png';
import outside from './images/covers/outside.png';
import pcWorld from './images/covers/pc-world.png';
import prevention from './images/covers/prevention.png';
import variety from './images/covers/variety.png';
import theWeek from './images/covers/the-week.png';
import tvGuide from './images/covers/tv-guide.png';

/** The three drift rates headless gave its cover layers. */
const SPEED = { sm: -5, md: -10, lg: -15 };

/** Position and drift rate per cover, in the order headless stacked them. */
const COVERS: {
  src: string;
  alt: string;
  size: 'sm' | 'md' | 'lg';
  speed: number;
  className: string;
}[] = [
  // left side
  {
    src: motorTrend,
    alt: 'Motor Trend magazine',
    size: 'sm',
    speed: SPEED.sm,
    className: 'xs:block left-36 top-16 md:-left-8 md:top-48 lg:left-0 lg:top-32 2xl:left-64 2xl:top-32',
  },
  {
    src: cooksIll,
    alt: 'Cooks Illustrated magazine',
    size: 'md',
    speed: SPEED.md,
    className: 'xs:block left-0 top-12 md:left-72 md:top-0 2xl:left-16 2xl:top-40',
  },
  {
    src: prevention,
    alt: 'Prevention magazine',
    size: 'sm',
    speed: SPEED.sm,
    className: 'md:block right-20 bottom-16 md:bottom-32 md:-right-8 2xl:-right-4 2xl:bottom-52',
  },
  {
    src: natGeo,
    alt: 'National Geographic magazine',
    size: 'lg',
    speed: SPEED.lg,
    className: 'xs:block left-12 top-44 sm:left-20 md:left-24 md:top-28 2xl:left-32 2xl:top-1/3',
  },
  {
    src: variety,
    alt: 'Variety magazine',
    size: 'md',
    speed: SPEED.md,
    className: 'md:block left-36 bottom-48 md:bottom-40 lg:left-52 lg:bottom-56 2xl:left-96 2xl:bottom-56',
  },
  // right side
  {
    src: tvGuide,
    alt: 'TV Guide magazine',
    size: 'sm',
    speed: SPEED.sm,
    className: 'xs:block -right-10 top-10 xs:top-4 xs:bottom-4 md:right-28 md:top-8 lg:right-28 lg:top-8 2xl:-right-12 2xl:top-24',
  },
  {
    src: newYork,
    alt: 'New York magazine',
    size: 'sm',
    speed: SPEED.sm,
    className: 'xs:block bottom-20 left-6 lg:left-16 lg:bottom-16',
  },
  {
    src: pcWorld,
    alt: 'PC World magazine',
    size: 'sm',
    speed: SPEED.sm,
    className: 'xs:block right-16 top-8 md:-right-16 md:top-16 2xl:right-9 2xl:top-40',
  },
  {
    src: newsweek,
    alt: 'Newsweek magazine',
    size: 'md',
    speed: SPEED.md,
    className: 'xs:block bottom-16 -left-4 md:-left-4 md:bottom-60 lg:-left-2 lg:bottom-60 2xl:left-44 2xl:bottom-52',
  },
  {
    src: natGeoKids,
    alt: 'National Geographic Kids magazine',
    size: 'md',
    speed: SPEED.md,
    className: 'xs:block -right-4 top-32 md:right-8 md:top-28 lg:-right-24 lg:top-48 2xl:right-32 2xl:bottom-1/3',
  },
  {
    src: outside,
    alt: 'Outside magazine',
    size: 'lg',
    speed: SPEED.lg,
    className: 'xs:block right-16 bottom-20 2xl:right-64 2xl:bottom-64',
  },
  {
    src: theWeek,
    alt: 'The Week magazine',
    size: 'md',
    speed: SPEED.md,
    className: 'xs:block right-4 bottom-32 md:right-0 md:bottom-56 2xl:right-6 2xl:bottom-32',
  },
];

type Props = {
  product: Product;
  addToCart: () => void;
  isAdding: boolean;
};

/**
 * The hero. Fills the screen below the navigation, so the chevron at its foot sits on
 * the fold and says there is more underneath.
 *
 * headless composed this out of react-scroll-parallax's stacked `layers`; here the same
 * stack is ordinary absolutely-positioned elements over a gradient, one to a ParallaxLayer
 * — which reproduces the box that component laid its layers out in, and without which the
 * composition below reads as the designer's classes but not as the designer's page. Every
 * position class is carried across unchanged.
 *
 * The price is the product's list price — the recurring monthly charge, which is what
 * this headline has always quoted. The free month is the CTA, not a discount on it.
 */
export default function MainPanel({ product, addToCart, isAdding }: Props) {
  const hero = useRef<HTMLDivElement>(null);
  const headerHeight = useHeaderHeight();
  const progress = useParallax(hero);
  const { currency } = useSite();
  const reviews = product.reviews_data;
  const height = `calc(100vh - ${headerHeight}px)`;

  return (
    <div
      ref={hero}
      className="relative overflow-hidden bg-gradient-to-b from-zinio-purple-100 to-white"
      style={{ height }}
    >
      <ParallaxLayer speed={5} progress={progress} zoom>
        <span className="absolute -left-48 top-32 lg:-left-8 lg:bottom-1/4 w-[400px]">
          <img src={ellipse} width={400} height={400} alt="" className="w-full h-auto" />
        </span>
      </ParallaxLayer>
      <ParallaxLayer speed={5} progress={progress} zoom>
        <span className="absolute -right-48 top-96 lg:-right-8 lg:bottom-1/3 w-[300px]">
          <img src={ellipse} width={300} height={300} alt="" className="w-full h-auto" />
        </span>
      </ParallaxLayer>
      <ParallaxLayer speed={10} progress={progress}>
        <span className="hidden sm:block absolute -left-14 top-1/2 sm:-left-20 lg:left-0 lg:bottom-1/3 w-32 lg:w-44">
          <img src={dots1} width={180} height={169} alt="" className="w-full h-auto" />
        </span>
      </ParallaxLayer>
      <ParallaxLayer speed={10} progress={progress} shiftX>
        <span className="hidden sm:block absolute right-0 -top-8 sm:-right-6 md:right-8 lg:-right-32 lg:top-1/3 w-32 sm:w-48">
          <img src={dots2} width={257} height={243} alt="" className="w-full h-auto" />
        </span>
      </ParallaxLayer>

      {COVERS.map((cover) => (
        <ParallaxLayer key={cover.alt} speed={cover.speed} progress={progress}>
          <DigitalMag src={cover.src} alt={cover.alt} size={cover.size} className={cover.className} />
        </ParallaxLayer>
      ))}

      <Container
        className="relative flex flex-col items-center justify-center text-center"
        style={{ height }}
      >
        <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl 2xl:text-7xl mb-5 lg:mb-7 font-bold max-w-4xl">
          Unlimited access to 5,000+ digital magazines
        </h1>
        <p className="text-md sm:text-xl md:text-xl 2xl:text-2xl font-semibold mb-8">
          Read on any device. Cancel anytime. Just{' '}
          <span className="font-bold">
            <Currency value={product.base_sku?.price ?? 0} currencyCode={currency} />
          </span>
          /month.
        </p>
        <Button
          size="lg"
          className="mb-10"
          iconAfter={faLongArrowRight}
          onClick={addToCart}
          isLoading={isAdding}
          loadingText="Adding to cart..."
        >
          Start free 1-month trial
        </Button>
        <span className="flex items-end mb-5">
          <span className="mr-3 font-semibold">Powered by</span>
          <img src={zinioLogo} width="125" height="42" alt="Zinio Unlimited logo" />
        </span>
        {reviews && (
          <div className="text-xl">
            <ReviewStars
              rating={reviews.rating}
              reviewsCount={reviews.count}
              onClick={() => document.getElementById('reviews')?.scrollIntoView({ behavior: 'smooth' })}
            />
          </div>
        )}
        <FontAwesomeIcon
          icon={faAngleDown}
          className="text-zinio-purple-600 opacity-25 absolute bottom-2 xs:bottom-6 sm:bottom-10 w-full"
          size="3x"
        />
      </Container>
    </div>
  );
}
